import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Components
import Checkbox from '../../../../../../components/Checkbox';
import Button from '../../../../../../components/CustomButton';
import TextInput from '../../../../../../components/TextInput';
import ViewEmailVerification from './VerificationCode';
import ViewChangeEmailSuccess from './Success';

// Context
import { useDataSettingsContext } from '../../context';

// Styles
import styles from './style.module.scss';
import viewsStyles from '../views.module.scss';

// Utils
import { validateEmail } from '../../../../../../utils/helpers';
import { userChangeEmailUrl } from '../../../../../../utils/urls';
import useCsrfToken from '../../../../../../utils/hooks/csfrToken';

type EmailView = 'form' | 'verification_code' | 'success';

const ViewChangeEmail: React.FC = () => {
  const { t } = useTranslation();
  const csrfToken = useCsrfToken();

  // =========================== States ===========================
  const [view, setView] = useState<EmailView>('form');
  const [loading, setLoading] = useState<boolean>(false);

  // Context
  const {
    user,
    onSetHeaderSubtitle,
    onSetHeaderTitle,
  } = useDataSettingsContext();

  // States
  const [email, setEmail] = useState<string>('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [acceptTerms, setAcceptTerms] = useState<boolean>(false);
  const [agreeMailing, setAgreeMailing] = useState<boolean>(false);

  // Ref
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Effects
  useEffect(() => {
    onSetHeaderSubtitle(t('profile.settings.data.change_email.description'));
    onSetHeaderTitle(t('profile.settings.data.change_email.title'));

    return () => {
      onSetHeaderSubtitle('');
      onSetHeaderTitle('');
    };
  }, [onSetHeaderSubtitle, onSetHeaderTitle, t]);

  // Handlers
  const handleChangeEmail = useCallback((value: string) => {
    const email = value;

    const isValid = validateEmail(email);

    setEmail(email);
    setEmailError(isValid ? null : t('profile.settings.data.change_email.error'));
  }, []);

  const sendVerificationEmail = useCallback(async () => {
    if (!email || emailError || !acceptTerms) return;

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('user[email]', email);

      const response = await fetch(userChangeEmailUrl(), {
        method: 'PATCH',
        body: formData,
        headers: {
          'X-CSRF-Token': csrfToken,
        },
      });

      if (response.ok) {
        setView('verification_code');
      } else {
        const data = await response.json().catch(() => ({}));
        const errorMsg = data?.errors?.[0] || data?.error || t('profile.settings.data.change_email.generic_error');
        setEmailError(errorMsg);
      }
    } catch (err) {
      console.error(err);
      setEmailError(t('profile.settings.data.change_email.network_error'));
    } finally {
      setLoading(false);
    }
  }, [email, emailError, acceptTerms, csrfToken, t]);

  const handleSendVerificationEmail = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    await sendVerificationEmail();
  }, [sendVerificationEmail]);

  if (view === 'verification_code') {
    return (
      <ViewEmailVerification
        email={email}
        loading={loading}
        onResendVerificationCode={sendVerificationEmail}
        onSuccess={() => setView('success')}
      />
    );
  }

  if (view === 'success') {
    return <ViewChangeEmailSuccess email={email} />;
  }

  return (
    <div className={viewsStyles.container} >
      <form onSubmit={handleSendVerificationEmail} className={viewsStyles.form} id='change-email-form'>
        {/* Current Email */}
        <TextInput
          label={t('profile.settings.data.change_email.current_email')}
          type='email'
          value={user?.email || ''}
          disabled
          readOnly
        />

        <TextInput
          label={t('profile.settings.data.change_email.new_email')}
          type='email'
          value={email || ''}
          onChange={handleChangeEmail}
          error={emailError}
          required
        />

        {/* Accept Terms */}
        <div className={viewsStyles.control_container}>
          <Checkbox
            id="accept_terms"
            checked={acceptTerms}
            onChange={setAcceptTerms}
            label={
              <div className={styles.accept_terms_label_container}>
                {t('profile.settings.data.change_email.accept_terms.title')}
                {/* Terms of Service todo: add link */}
                <a href='https://www.studypal.ai/terms-of-service' target="_blank" className={styles.link} rel="noopener noreferrer">
                  {t('profile.settings.data.change_email.accept_terms.terms')}
                </a>
                {t('profile.settings.data.change_email.accept_terms.and')}
                {/* Privacy Policy todo: add link */}
                <a href='https://www.studypal.ai/privacy-policy' target="_blank" className={styles.link} rel="noopener noreferrer">
                  {t('profile.settings.data.change_email.accept_terms.privacy_policy')}
                </a>
              </div>
            }
          />

          <Checkbox
            id="agree_mailing_list"
            checked={agreeMailing}
            onChange={setAgreeMailing}
            label={t('profile.settings.data.change_email.agree_mailing_list')}
          />
        </div>

        <div className={viewsStyles.actions}>
          <Button
            variant='primary'
            type='submit'
            classes={{
              root: viewsStyles.actions_button,
            }}
            disabled={loading || !email || !acceptTerms || emailError !== null}
          >
            {t('profile.settings.data.change_email.send_verification_email')}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default ViewChangeEmail;
